import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import "./ooraInfraDataset.css";
import { updateOoraInfraDataset } from "../../Redux/Actions/ooraInfraDatasetActions";
import AddImage from "../../Global Functions/addImage";
import { rowToForm, validateForm, buildPayload } from "../../Utils/ooraInfraDatasetHelpers";
import OoraInfraMetaFields from "./OoraInfraMetaFields";

const EMPTY_DOC = { url: "", title: "", description: "", fileName: "", uploading: false };

const getFileName = (url = "") => decodeURIComponent(url.split("/").pop() || "");

const UpdateOoraInfraDataset = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const row = state?.row;
  const { updateLoading = false } = useSelector((state) => state.ooraInfraDataset || {});

  const [form, setForm] = useState(rowToForm(row));
  const [documents, setDocuments] = useState(
    row?.documents?.length
      ? row.documents.map((d) => ({
          url: d.url || "",
          title: d.title || "",
          description: d.description || "",
          fileName: getFileName(d.url),
          uploading: false,
        }))
      : [{ ...EMPTY_DOC }]
  );

  // opened directly / refreshed: no row data, go back to list
  useEffect(() => {
    if (!row) navigate("/oora-infra-dataset", { replace: true });
  }, [row, navigate]);

  const anyUploading = documents.some((d) => d.uploading);
  const busy = updateLoading || anyUploading;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDocChange = (index, field, value) => {
    setDocuments((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  const addDocRow = () => setDocuments((prev) => [...prev, { ...EMPTY_DOC }]);

  const removeDocRow = (index) => setDocuments((prev) => prev.filter((_, i) => i !== index));

  const handleFile = async (index, file) => {
    if (!file) return;
    handleDocChange(index, "uploading", true);
    try {
      const url = await AddImage(file);
      setDocuments((prev) =>
        prev.map((d, i) =>
          i === index
            ? { ...d, url, fileName: file.name, uploading: false, title: d.title || file.name.replace(/\.[^/.]+$/, "") }
            : d
        )
      );
    } catch (error) {
      handleDocChange(index, "uploading", false);
      toast.error("File upload failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) return toast.error(error);
    if (!documents.length) return toast.error("At least one document is required");
    for (let i = 0; i < documents.length; i++) {
      if (!documents[i].url) return toast.error(`Document ${i + 1}: please upload a file`);
      if (!documents[i].title.trim()) return toast.error(`Document ${i + 1}: title is required`);
    }

    const response = await dispatch(
      updateOoraInfraDataset({
        id,
        ...buildPayload(form),
        documents: documents.map((d) => ({
          url: d.url,
          title: d.title.trim(),
          description: d.description.trim(),
        })),
      })
    );

    if (response?.statusCode === 200) {
      toast.success("Dataset updated successfully");
      navigate("/oora-infra-dataset");
    } else {
      toast.error(response?.message || "Something went wrong");
    }
  };

  // open file in a new tab instead of downloading
  const openDocument = async (e, url) => {
    e.preventDefault();
    const win = window.open("", "_blank");
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const isPdf = url.toLowerCase().split("?")[0].endsWith(".pdf");
      const objUrl = URL.createObjectURL(new Blob([blob], { type: isPdf ? "application/pdf" : blob.type }));
      win.location.href = objUrl;
    } catch (error) {
      win.location.href = url; // fallback (CORS blocked)
    }
  };

  if (!row) return null;

  return (
    <>
      <div className="oid-wrapper">
        <div className="oid-header">
          <div className="oid-header-left">
            <div className="oid-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            </div>
            <div>
              <h1 className="oid-title">Update Dataset</h1>
              <p className="oid-subtitle">Edit details or replace uploaded documents</p>
            </div>
          </div>
        </div>

        <form className="oid-card" onSubmit={handleSubmit}>
          <div className="oid-form-body">
            <OoraInfraMetaFields form={form} onChange={handleChange} disabled={busy} />

            <div className="oid-docs-head">
              <h3>Documents</h3>
              <button type="button" className="oid-btn oid-btn--ghost oid-btn--sm" onClick={addDocRow} disabled={busy}>
                + Add Document
              </button>
            </div>

            {documents.map((doc, index) => (
              <div className="oid-doc" key={index}>
                <div className="oid-doc-top">
                  <span className="oid-section-badge">{String(index + 1).padStart(2, "0")}</span>
                  {documents.length > 1 && (
                    <button type="button" className="oid-remove" onClick={() => removeDocRow(index)} disabled={busy}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="oid-grid-2">
                  <div className="oid-field">
                    <label className="oid-label">Document Title <span className="oid-req">*</span></label>
                    <input
                      className="oid-input"
                      type="text"
                      value={doc.title}
                      onChange={(e) => handleDocChange(index, "title", e.target.value)}
                      placeholder="e.g. Sector Layout Plan"
                      disabled={busy}
                    />
                  </div>
                  <div className="oid-field">
                    <label className="oid-label">Description <span className="oid-optional">(Optional)</span></label>
                    <input
                      className="oid-input"
                      type="text"
                      value={doc.description}
                      onChange={(e) => handleDocChange(index, "description", e.target.value)}
                      placeholder="Short description"
                      disabled={busy}
                    />
                  </div>
                  <div className="oid-field oid-field--full">
                    <label className="oid-label">File <span className="oid-req">*</span></label>
                    <label className={`oid-upload ${doc.url ? "oid-upload--done" : ""}`}>
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          handleFile(index, e.target.files[0]);
                          e.target.value = "";
                        }}
                        disabled={busy}
                      />
                      {doc.uploading ? (
                        <span>Uploading...</span>
                      ) : doc.url ? (
                        <span className="oid-file-name">{doc.fileName} — click to replace</span>
                      ) : (
                        <span>Click to choose a file</span>
                      )}
                    </label>
                    {doc.url && !doc.uploading && (
                      <a href={doc.url} onClick={(e) => openDocument(e, doc.url)} target="_blank" rel="noreferrer" className="oid-view-link">
                        View current file
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="oid-form-foot">
            <button type="button" className="oid-btn oid-btn--ghost" onClick={() => navigate("/oora-infra-dataset")} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="oid-btn oid-btn--submit" disabled={busy}>
              {updateLoading ? "Saving..." : "Update Dataset"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default UpdateOoraInfraDataset;